import React, { createRef, ReactNode, useContext } from "react";

import { SvgIcon } from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { ReactComponent as HotelBuildingIcon } from "images/icons/building.svg";
import { ReactComponent as VolleyballIcon } from "images/icons/volleyball.svg";
import { ReactComponent as CarIcon } from "images/icons/car.svg";
import { ReactComponent as PlaneIcon } from "images/icons/plane.svg";
import { Region } from "../../templates/fragments/region";
import { Resort } from "../../templates/fragments/resort";
import { RegionResortContext } from "../destination/region-resort-core";
import { useAfilsUrls } from "./useAfilsUrls";
import { useStaticQuery, graphql } from "gatsby";
import { GatsbyImage, getImage, ImageDataLike } from "gatsby-plugin-image";
import { useAirportNames } from "./useAirportNames";

declare global {
    interface Window {
        dataLayer: Record<string, any>[];
    }
}

const textMap = {
    flightTickets: "Flights",
    carRentals: "Car rentals",
    hotelBookings: "Hotels",
    activities: "Activities"
};

const IconWrapper = ({
    icon,
    fill,
    type,
    secondaryText,
    className,
    location,
    url,
    image
}: {
    icon: any;
    fill?: boolean;
    className?: string;
    type: string;
    secondaryText?: ReactNode | string;
    location: Region | Resort;
    url: string;
    image: ImageDataLike;
}) => {
    const continent =
        (location as Resort).region?.country.continent ||
        (location as Region).country.continent;
    const onLinkClick = () => {
        window.dataLayer.push({
            event: "afil_landing",
            type,
            continent,
            subId: 1,
            slug: location.slug
        });
        window.open(url, "_blank");
    };
    const coverPhoto = getImage(image);

    return (
        <div
            onClick={onLinkClick} // eslint-disable-line
            className={`${className} flex max-w-[300px] cursor-pointer flex-col rounded-md border-2 border-solid border-zinc-100 bg-zinc-50 p-[10px] transition-colors hover:text-sh-red xm:p-[16px] lg:p-[20px]`}
        >
            <GatsbyImage
                image={coverPhoto}
                alt={""}
                objectFit="cover"
                className="rounded-md"
            />
            <div className="mt-[10px] mb-[4px] text-lg font-bold xm:text-xl md:text-2xl">
                {textMap[type]}
            </div>
            <div className="flex flex-1 items-start justify-between gap-[4px]">
                <div className="text-[13px] leading-tight text-zinc-400 md:text-sm min-h-[2.5rem]">
                    {secondaryText || ""}
                </div>
                <ArrowForwardOutlinedIcon
                    color="primary"
                    className="!h-[28px] !w-[28px] self-end rounded-full bg-sh-50 p-[4px] md:!h-[36px] md:!w-[36px] md:p-[8px] "
                />
            </div>
        </div>
    );
};

export const AffiliateTabs = () => {
    const data = useStaticQuery(graphql`
        {
            flightTickets: file(relativePath: { eq: "plane.jpg" }) {
                childImageSharp {
                    gatsbyImageData(width: 270, height: 120)
                }
            }
            carRentals: file(relativePath: { eq: "car_rentals.jpg" }) {
                childImageSharp {
                    gatsbyImageData(width: 270, height: 120)
                }
            }
            hotelBookings: file(relativePath: { eq: "hotel.jpg" }) {
                childImageSharp {
                    gatsbyImageData(width: 270, height: 120)
                }
            }
            activities: file(relativePath: { eq: "quads.jpg" }) {
                childImageSharp {
                    gatsbyImageData(width: 270, height: 120)
                }
            }
        }
    `);
    const scriptWrapperRef = createRef<HTMLDivElement>();
    const location = useContext(RegionResortContext);
    const isResort = location.hasOwnProperty("region");
    const { viatorUrl, ebUrl, kiwiUrl, expName, expUrl, viatorName } =
        useAfilsUrls({
            afData: location.afData,
            resort: isResort ? (location as Resort) : undefined,
            region: !isResort ? (location as Region) : undefined
        });
    const airCode = location.afData?.airCode;
    const [origin, destination] = useAirportNames(airCode);
    const getAirportRow = (airport: { code: string; name: string }) => {
        return origin?.code && origin?.name ? (
            <>
                <span className="font-bold">{airport?.code}</span>:&nbsp;
                {airport.name}
            </>
        ) : null;
    };
    const fromText = getAirportRow(origin);
    const toText = getAirportRow(destination);
    return (
        <>
            <div ref={scriptWrapperRef} className="invisible"></div>

            <div className="mx-auto grid w-11/12 max-w-[1200px] grid-cols-1 items-stretch justify-items-center gap-[10px] xm:grid-cols-2 md:w-10/12 md:gap-[20px] mdmax:w-11/12">
                <IconWrapper
                    location={location}
                    icon={PlaneIcon}
                    type="flightTickets"
                    url={kiwiUrl}
                    secondaryText={
                        airCode && (
                            <div>
                                {fromText} to
                                <br />
                                {toText}
                            </div>
                        )
                    }
                    className="justify-self-end xmmax:order-2 xmmax:justify-self-center"
                    image={data.flightTickets.childImageSharp.gatsbyImageData}
                />
                <IconWrapper
                    location={location}
                    icon={VolleyballIcon}
                    fill
                    type="activities"
                    url={viatorUrl}
                    secondaryText={viatorName && `in ${viatorName}`}
                    className="justify-self-start xmmax:order-1 xmmax:justify-self-center"
                    image={data.activities.childImageSharp.gatsbyImageData}
                />
            </div>
        </>
    );
};
